/* This component helps new users get engaged with the platform. It:
   1. Asks them what they are interested in learning, and what their learning habits are
   2. Asks them to make an account (email + password), so we can send them email to re-engage
      and so that it would be harder for them to lose their account
   3. Asks them to sign up for a paid account for more features
*/

import { IonModal, IonButton, IonInput, IonItem, IonLabel, IonHeader, IonTitle, IonToolbar, IonList, IonText, IonTextarea, IonIcon, IonButtons } from "@ionic/react";
import { useEffect, useState } from "react";
import prefs from '@/controllers/preferences'
import backend from "@/controllers/backend";

enum Role {
    null = "",
    student = "student",
    instructor = "instructor",
    parent = "parent"
}

const settingsName = 'onboarding_settings';
const defaults = {
    screenNumber: 0,
    loginScreen: false,

    role: Role.null,

    interests: "",
    preferences: "",
    habits: "",

    name: "",
    pronouns: "",

    llm_provider_url: "",
    llm_provider_key: "",
    llm_provider_model: "",
    whisper_provider_url: "",
    whisper_provider_key: "",
    whisper_provider_model: "",
    jupyter_url: "",
    jupyter_key: "",

    done: false // should we show the form again on the next visit?
};

//prefs.set(settingsName, defaults)

const get = async() => await prefs.get_or_set(settingsName, defaults)

import OnboardingHandlers from './OnboardingHandlers'
import { closeOutline } from "ionicons/icons";

const Onboarding = () => {
    const [settings, setSettings] = useState({...defaults, done: true});
    const loadSettings = async () => setSettings(await get());

    useEffect(() => {
        loadSettings();
    }, [])

    const updateSettings = async (change) => {
        for (const k of Object.keys(change)) {
            if (!change[k]) change[k] = defaults[k];
        }

        const newSettings = {...await get(), ...change};
        await prefs.set(settingsName, newSettings);

        setSettings(newSettings)

        backend.onboarding_save_data({
            screenNumber: newSettings.screenNumber,
            loginScreen: newSettings.loginScreen,

            role: newSettings.role,

            interests: newSettings.interests,
            preferences: newSettings.preferences,
            habits: newSettings.habits,

            name: newSettings.name,
            pronouns: newSettings.pronouns,

            done: newSettings.done
        })
    }

    OnboardingHandlers.openModal = () => {
        updateSettings({ done: true })
        setTimeout(() => updateSettings({ done: false }), 10);
    }

    //if (settings.done) return;

    const [errorMsg, setErrorMsg] = useState("")

    if (settings.done) return;

    if (settings.loginScreen)
    return (
        <IonModal isOpen={!settings.done} style={{'--height': 'fit-content', '--max-width': '26em' }}>
            <IonHeader>
                <IonToolbar>
                    <IonTitle>Sign In</IonTitle>
                </IonToolbar>
            </IonHeader>
            <form className="overflow-auto" style={{'maxHeight': '80vh', 'overflowY': 'auto'}} onSubmit={async (e) => {
                e.preventDefault();
                const formData = new FormData(e.target as HTMLFormElement);
                console.log(formData);

                const r = await backend.onboarding_login(
                    formData.get("email"), formData.get("current-password")
                );
                if (!r.success) {
                    setErrorMsg(r.error)
                } else {
                    updateSettings({ done: true })
                }
                
            }}>
                <IonText>
                    <p className="py-2 px-3">
                        Welcome back!
                    </p>
                </IonText>
            
                <IonList>
                    <IonItem>
                        <IonLabel position="stacked">Email</IonLabel>
                        <IonInput type="email" name="email" autocomplete="email" placeholder="your@example.com" />
                    </IonItem>

                    <IonItem>
                        <IonLabel position="stacked">Password</IonLabel>
                        <IonInput type="password" name="current-password" autocomplete="current-password" placeholder="Enter a secure password" />
                    </IonItem>
                </IonList>
                {errorMsg && <p className="py-2 px-3">
                    Error: {errorMsg}
                </p>}

                <IonButton type="submit" expand="full" className="mt-2">
                    Sign In
                </IonButton>
                <IonButton fill="clear" expand="full" onClick={() => updateSettings({ loginScreen: false })} className="mt-2">
                    Back
                </IonButton>
            </form>
        </IonModal>
    );

    if (settings.screenNumber == 0)
    return (
        <IonModal isOpen={!settings.done} style={{'--height': 'fit-content', '--max-width': '26em' }}>
            <IonHeader>
                <IonToolbar>
                    <IonTitle>Welcome to Convolution!</IonTitle>
                    <IonButtons slot="secondary">
                    <IonButton fill="clear" onClick={(e) => e.target.closest('ion-modal').dismiss()}>
                        <IonIcon slot="icon-only" icon={closeOutline}></IonIcon>
                    </IonButton>
                    </IonButtons>
                </IonToolbar>
            </IonHeader>
            <div className="overflow-auto" style={{'maxHeight': '80vh', 'overflowY': 'auto'}}>
                <IonText>
                    <p className="py-2 px-3">
                        Convolution is an agentic online learning platform that helps you learn, teach and supervise.
                    </p>
                    <p className="py-2 px-3">Please choose your role to get started:</p>
                </IonText>

                <div className="m-auto w-fit">
                <IonButton onClick={() => {
                    updateSettings({ role: Role.student, screenNumber: settings.screenNumber + 1 })
                }} className="mt-2">
                    Student
                </IonButton>
                <IonButton fill="clear" onClick={() => {
                    updateSettings({ role: Role.instructor, screenNumber: settings.screenNumber + 1 })
                }} className="mt-2">
                    Instructor
                </IonButton>
                <IonButton fill="clear" onClick={() => {
                    updateSettings({ role: Role.parent, screenNumber: settings.screenNumber + 1 })
                }} className="mt-2">
                    Parent
                </IonButton>
                </div>
                <IonButton fill="clear" expand="full" onClick={() => updateSettings({ loginScreen: true })} className="mt-2">
                    I already have an account
                </IonButton>
                <IonButton fill="clear" expand="full" onClick={(e) => e.target.closest('ion-modal').dismiss()} className="mt-2">
                    Later
                </IonButton>
            </div>
        </IonModal>
    );

    if (settings.screenNumber == 1)
    return (
        <IonModal isOpen={!settings.done} style={{'--height': 'fit-content', '--max-width': '26em' }}>
            <IonHeader>
                <IonToolbar>
                    <IonTitle>{settings.role == Role.instructor ? "Teaching" : "Learning"} Preferences</IonTitle>
                    <IonButtons slot="secondary">
                    <IonButton fill="clear" onClick={(e) => e.target.closest('ion-modal').dismiss()}>
                        <IonIcon slot="icon-only" icon={closeOutline}></IonIcon>
                    </IonButton>
                    </IonButtons>
                </IonToolbar>
            </IonHeader>
            <div className="overflow-auto" style={{'maxHeight': '80vh', 'overflowY': 'auto'}}>
                <IonText>
                    <p className="py-2 px-3">
                        To get started, please tell us a bit about your {settings.role == Role.instructor ? "teaching" : "learning"} interests, preferences and habits.
                    </p>
                </IonText>
            
                <IonList>
                    <IonItem>
                        <IonLabel position="stacked">What are you interested in {settings.role == Role.instructor ? "teaching" : "learning"}?</IonLabel>
                        <IonTextarea autoGrow={true} value={settings.interests} onIonChange={(e) =>
                            updateSettings({ interests: e.detail.value! })
                        } placeholder="e.g., Web Development, Data Science" />
                    </IonItem>

                    <IonItem>
                        <IonLabel position="stacked">What are your {settings.role == Role.instructor ? "teaching" : "learning"} preferences?</IonLabel>
                        <IonTextarea autoGrow={true} value={settings.preferences} onIonChange={(e) =>
                            updateSettings({ preferences: e.detail.value! })
                        } placeholder={settings.role == Role.instructor ? "e.g. Mind Maps, Code Labs" : "e.g., Visual Learner"} />
                    </IonItem>

                    <IonItem>
                        <IonLabel position="stacked">What {settings.role == Role.instructor ? "is your teaching schedule" : "are your learning habits"}?</IonLabel>
                        <IonTextarea autoGrow={true} value={settings.habits} onIonChange={(e) =>
                            updateSettings({ habits: e.detail.value! })
                        } placeholder="e.g., Daily, Weekly" />
                    </IonItem>
                </IonList>

                <IonButton expand="full" onClick={() => updateSettings({ screenNumber: settings.screenNumber + 1 })} className="mt-2">
                    Next
                </IonButton>
                <IonButton fill="clear" expand="full" onClick={() => updateSettings({ screenNumber: settings.screenNumber - 1 })} className="mt-2">
                    Back
                </IonButton>
                <IonButton fill="clear" expand="full" onClick={(e) => e.target.closest('ion-modal').dismiss()} className="mt-2">
                    Later
                </IonButton>
            </div>
        </IonModal>
    );

    if (settings.screenNumber == 2)
    return (
        <IonModal isOpen={!settings.done} style={{'--height': 'fit-content', '--max-width': '26em' }}>
            <IonHeader>
                <IonToolbar>
                    <IonTitle>Interaction Preferences</IonTitle>
                    <IonButtons slot="secondary">
                    <IonButton fill="clear" onClick={(e) => e.target.closest('ion-modal').dismiss()}>
                        <IonIcon slot="icon-only" icon={closeOutline}></IonIcon>
                    </IonButton>
                    </IonButtons>
                </IonToolbar>
            </IonHeader>
            <div className="overflow-auto" style={{'maxHeight': '80vh', 'overflowY': 'auto'}}>
                <IonText>
                    <p className="py-2 px-3">
                        To help personalize your lessons, please tell us how you like to be called.
                    </p>
                </IonText>
            
                <IonList>
                    <IonItem>
                        <IonLabel position="stacked">Name</IonLabel>
                        <IonInput value={settings.name} onIonChange={(e) =>
                            updateSettings({ name: e.detail.value! })
                        } placeholder="e.g., Einstein" />
                    </IonItem>

                    <IonItem>
                        <IonLabel position="stacked">Pronouns</IonLabel>
                        <IonInput value={settings.pronouns} onIonChange={(e) =>
                            updateSettings({ pronouns: e.detail.value! })
                        } placeholder="e.g., she/her, he/him" />
                    </IonItem>
                </IonList>

                <IonButton expand="full" onClick={() => updateSettings({ screenNumber: settings.screenNumber + 1 })} className="mt-2">
                    Next
                </IonButton>
                <IonButton fill="clear" expand="full" onClick={() => updateSettings({ screenNumber: settings.screenNumber - 1 })} className="mt-2">
                    Back
                </IonButton>
                <IonButton fill="clear" expand="full" onClick={(e) => e.target.closest('ion-modal').dismiss()} className="mt-2">
                    Later
                </IonButton>
            </div>
        </IonModal>
    );

    if (settings.screenNumber == 3)
    return (
        <IonModal isOpen={!settings.done} style={{'--height': 'fit-content', '--max-width': '26em' }}>
            <IonHeader>
                <IonToolbar>
                    <IonTitle>Sign Up</IonTitle>
                    <IonButtons slot="secondary">
                    <IonButton fill="clear" onClick={(e) => e.target.closest('ion-modal').dismiss()}>
                        <IonIcon slot="icon-only" icon={closeOutline}></IonIcon>
                    </IonButton>
                    </IonButtons>
                </IonToolbar>
            </IonHeader>
            <form className="overflow-auto" style={{'maxHeight': '80vh', 'overflowY': 'auto'}} onSubmit={async (e) => {
                e.preventDefault();
                const formData = new FormData(e.target as HTMLFormElement);
                console.log(formData);

                const r = await backend.onboarding_signup(
                    formData.get("email"), formData.get("new-password"), formData.get("parent-email")
                );
                if (!r.success) {
                    setErrorMsg(r.error)
                } else {
                    updateSettings({ screenNumber: settings.screenNumber + 1 })
                }                
            }}>
                <IonText>
                    <p className="py-2 px-3">
                        To make sure you don't lose your data, please sign up.
                    </p>
                    {settings.role == Role.parent && <p className="py-2 px-3">
                        To link your child account, begin by creating a parent account. Once that’s set up, simply use your parent email to sign up for your child's account.
                    </p>}
                </IonText>
            
                <IonList>
                    <IonItem>
                        <IonLabel position="stacked">Email</IonLabel>
                        <IonInput type="email" name="email" autocomplete="email" placeholder="your.email@example.com" />
                    </IonItem>

                    <IonItem>
                        <IonLabel position="stacked">Password</IonLabel>
                        <IonInput type="password" name="new-password" autocomplete="new-password" placeholder="Enter a secure password" />
                    </IonItem>

                    {settings.role == Role.student && <IonItem>
                        <IonLabel position="stacked">Parent Email</IonLabel>
                        <IonInput type="email" name="parent-email" placeholder="your.parent@example.com" />
                    </IonItem>}
                </IonList>
                {errorMsg && <p className="py-2 px-3">
                    Error: {errorMsg}
                </p>}

                <IonButton type="submit" expand="full" className="mt-2">
                    Signup
                </IonButton>
                <IonButton fill="clear" expand="full" onClick={() => updateSettings({ screenNumber: settings.screenNumber + 1 })} className="mt-2">
                    Maybe later
                </IonButton>
                <IonButton fill="clear" expand="full" onClick={() => updateSettings({ screenNumber: settings.screenNumber - 1 })} className="mt-2">
                    Back
                </IonButton>
            </form>
        </IonModal>
    );

    if (settings.screenNumber == 4)
    return (
        <IonModal isOpen={!settings.done} style={{'--max-width': '26em', '--height': 'fit-content' }}>
            <IonHeader>
                <IonToolbar>
                    <IonTitle>Setup Payments</IonTitle>
                    <IonButtons slot="secondary">
                    <IonButton fill="clear" onClick={(e) => e.target.closest('ion-modal').dismiss()}>
                        <IonIcon slot="icon-only" icon={closeOutline}></IonIcon>
                    </IonButton>
                    </IonButtons>
                </IonToolbar>
            </IonHeader>
            <div className="overflow-auto" style={{'maxHeight': '80vh', 'overflowY': 'auto'}}>
                <IonText className="markdown">
                    <p className="pt-2 px-3">
                        Consider subscribing to get:
                    </p>
                    <ol>
                        <li>More reliable service. LLM availability for free accounts are subject to demand and can be overloaded at times</li>
                        <li>Your own dedicated computing environment with unrestricted internet access</li>
                        <li>Better text-to-speech models</li>
                    </ol>
                    <p className="pt-2 px-3">
                        If you choose to stay on a free account, you will also be able to setup your own LLM provider and computing environment later.
                    </p>
                </IonText>

                <IonButton expand="full" onClick={() => updateSettings({ screenNumber: settings.screenNumber + 1 })} className="mt-2">
                    Let's go
                </IonButton>
                <IonButton fill="clear" expand="full" onClick={() => updateSettings({ screenNumber: settings.screenNumber + 2 })} className="mt-2">
                    Maybe later
                </IonButton>
                <IonButton fill="clear" expand="full" onClick={() => updateSettings({ screenNumber: settings.screenNumber - 1 })} className="mt-2">
                    Back
                </IonButton>
            </div>
        </IonModal>
    );

    if (settings.screenNumber == 5)
    return (
        <IonModal isOpen={!settings.done} style={{'--height': 'fit-content', '--max-width': '26em' }}>
            <IonHeader>
                <IonToolbar>
                    <IonTitle>Setup Payments</IonTitle>
                    <IonButtons slot="secondary">
                    <IonButton fill="clear" onClick={(e) => e.target.closest('ion-modal').dismiss()}>
                        <IonIcon slot="icon-only" icon={closeOutline}></IonIcon>
                    </IonButton>
                    </IonButtons>
                </IonToolbar>
            </IonHeader>
            <div className="overflow-auto" style={{'maxHeight': '80vh', 'overflowY': 'auto'}}>
                <IonText className="markdown">
                    <p className="pt-2 px-3">
                        Our monthly subscription is just $5. This supports our upstream API costs until your funds are used up. If your balance runs out, we’ll kindly request a top-up. Thank you!
                    </p>
                    <p className="pt-2 px-3">
                        To protect your privacy, we encourage you to pay with Bitcoin.
                        Alternatively, you can also use your credit card through Stripe.
                    </p>
                    <div className="m-auto w-full flex flex-wrap">
                        <div className="flex-1">
                            <IonButton fill="clear" expand="full" onClick={async () => {
                                const newTab = window.open('', '_blank')!;
                                console.log('btc_pay newTab', newTab);
                                const url = await backend.onboarding_pay_btc();
                                newTab.location.href = url;
                                newTab.focus();
                                //window.open(url, '_blank')!.focus();
                            }}>
                                <IonIcon slot="start" aria-hidden="true" src="/bitcoin.svg" size="large" />
                                Bitcoin
                            </IonButton>
                        </div>
                        <div className="flex-1">
                            <IonButton fill="clear" expand="full" target="_blank" href="https://buy.stripe.com/14A5kEcqigis87z411bwk00">
                                <IonIcon slot="start" aria-hidden="true" src="/stripe_square.svg" size="large" />
                                Stripe
                            </IonButton>
                        </div>
                    </div>
                    <p className="pt-2 px-3">
                            Any remaining balance from your $5 subscription at the end of the month will be kept to support our ongoing services. Thank you for contributing to our community!
                    </p>
                </IonText>

                <IonButton expand="full" onClick={() => updateSettings({ screenNumber: settings.screenNumber + 1 })} className="mt-2">
                    Next
                </IonButton>
                <IonButton fill="clear" expand="full" onClick={() => updateSettings({ screenNumber: settings.screenNumber - 1 })} className="mt-2">
                    Back
                </IonButton>
            </div>
        </IonModal>
    );

    if (settings.screenNumber == 6)
    return (
        <IonModal isOpen={!settings.done} style={{'--height': 'fit-content', '--max-width': '26em' }}>
            <IonHeader>
                <IonToolbar>
                    <IonTitle>Bring Your Own Compute</IonTitle>
                    <IonButtons slot="secondary">
                    <IonButton fill="clear" onClick={(e) => e.target.closest('ion-modal').dismiss()}>
                        <IonIcon slot="icon-only" icon={closeOutline}></IonIcon>
                    </IonButton>
                    </IonButtons>
                </IonToolbar>
            </IonHeader>
            <div className="overflow-auto" style={{'maxHeight': '80vh', 'overflowY': 'auto'}}>
                <IonText className="markdown">
                    <p className="pt-2 px-3">
                        You can connect your own LLM, Whisper and Jupyter providers.
                        Would you like to do that? It might require some technical expertise.
                    </p>
                    <p className="pt-2 px-3">
                        This will allow you to use this website completely for free, but you have
                        to manage things yourself.
                    </p>
                    <p className="pt-2 px-3">
                        In particular, if you decide to self-host Jupyter, make sure to put it in an isolated
                        environment like a virtual machine or a dedicated computer.
                    </p>
                </IonText>

                <IonButton expand="full" onClick={() => updateSettings({ screenNumber: settings.screenNumber + 1 })} className="mt-2">
                    Sure
                </IonButton>
                <IonButton fill="clear" expand="full" onClick={() => updateSettings({ screenNumber: settings.screenNumber + 2 })} className="mt-2">
                    Maybe later
                </IonButton>
                <IonButton fill="clear" expand="full" onClick={() => updateSettings({ screenNumber: settings.screenNumber - 1 })} className="mt-2">
                    Back
                </IonButton>
            </div>
        </IonModal>
    );

    if (settings.screenNumber == 7)
    return (
        <IonModal isOpen={!settings.done} style={{'--height': 'fit-content', '--max-width': '34em' }}>
            <IonHeader>
                <IonToolbar>
                    <IonTitle>Setup Compute</IonTitle>
                    <IonButtons slot="secondary">
                    <IonButton fill="clear" onClick={(e) => e.target.closest('ion-modal').dismiss()}>
                        <IonIcon slot="icon-only" icon={closeOutline}></IonIcon>
                    </IonButton>
                    </IonButtons>
                </IonToolbar>
            </IonHeader>
            <div className="overflow-auto" style={{'maxHeight': '80vh', 'overflowY': 'auto'}}>
                <IonText>
                    <p className="py-2 px-3">
                       Data entered here is stored only in your browser and not sent to the server.
                    </p>
                    <p className="py-2 px-3">
                       If you are running Ollama, make sure to relax CORS rules:
                    </p>
                    <p className="py-2 px-3 text-nowrap overflow-x-auto">OLLAMA_HOST=0.0.0.0 OLLAMA_ORIGINS=* ollama serve</p>
                    <p className="py-2 px-3">
                        Same thing for Jupyter:
                    </p>
                    <p className="py-2 px-3 text-nowrap overflow-x-auto">
                        --ServerApp.allow_origin='*' --ServerApp.tornado_settings=" &#123;'headers':  &#123;'Access-Control-Allow-Headers': 'baggage, authorization, sentry-trace, x-xsrftoken, Origin, Accept, Content-Type' &#125; &#125;" --MappingKernelManager.cull_idle_timeout=600 --MappingKernelManager.cull_interval=60 --MappingKernelManager.cull_connected=False
                    </p>
                    <p className="py-2 px-3">To ensure both your safety and that of others, we kindly recommend running Jupyter on a dedicated computer or within a virtual machine. This is important because Jupyter provides complete access to the environment in which it operates. Thank you for your understanding!</p>
                </IonText>
            
                <IonList>
                    <IonItem>
                        <IonLabel position="stacked">LLM Provider Endpoint (OpenAI compatible)</IonLabel>
                        <IonInput value={settings.llm_provider_url} onIonChange={(e) =>
                            updateSettings({ llm_provider_url: e.detail.value! })
                        } placeholder="e.g., https://generativelanguage.googleapis.com/v1beta/openai" />
                    </IonItem>

                    <IonItem>
                        <IonLabel position="stacked">LLM Provider API Key</IonLabel>
                        <IonInput value={settings.llm_provider_key} onIonChange={(e) =>
                            updateSettings({ llm_provider_key: e.detail.value! })
                        } placeholder="e.g., ABCD..." />
                    </IonItem>

                    <IonItem>
                        <IonLabel position="stacked">LLM Model Name</IonLabel>
                        <IonInput value={settings.llm_provider_model} onIonChange={(e) =>
                            updateSettings({ llm_provider_model: e.detail.value! })
                        } placeholder="e.g., gpt-oss-120b" />
                    </IonItem>

                    <IonItem>
                        <IonLabel position="stacked">Whisper Provider Endpoint (OpenAI compatible)</IonLabel>
                        <IonInput value={settings.whisper_provider_url} onIonChange={(e) =>
                            updateSettings({ whisper_provider_url: e.detail.value! })
                        } placeholder="e.g., https://api.groq.com/openai/v1" />
                    </IonItem>

                    <IonItem>
                        <IonLabel position="stacked">Whisper Provider API Key</IonLabel>
                        <IonInput value={settings.whisper_provider_key} onIonChange={(e) =>
                            updateSettings({ whisper_provider_key: e.detail.value! })
                        } placeholder="e.g., EFGH..." />
                    </IonItem>

                    <IonItem>
                        <IonLabel position="stacked">Whisper Model Name</IonLabel>
                        <IonInput value={settings.whisper_provider_model} onIonChange={(e) =>
                            updateSettings({ whisper_provider_model: e.detail.value! })
                        } placeholder="e.g., whisper-large-v3" />
                    </IonItem>

                    <IonItem>
                        <IonLabel position="stacked">Jupyter Endpoint</IonLabel>
                        <IonInput value={settings.jupyter_url} onIonChange={(e) =>
                            updateSettings({ jupyter_url: e.detail.value! })
                        } placeholder="e.g., http://localhost:8888/" />
                    </IonItem>

                    <IonItem>
                        <IonLabel position="stacked">Jupyter Token</IonLabel>
                        <IonInput value={settings.jupyter_key} onIonChange={(e) =>
                            updateSettings({ jupyter_key: e.detail.value! })
                        } placeholder="e.g., IJKL..." />
                    </IonItem>
                </IonList>

                <IonButton expand="full" onClick={() => updateSettings({ screenNumber: settings.screenNumber + 1 })} className="mt-2">
                    Next
                </IonButton>
                <IonButton fill="clear" expand="full" onClick={() => updateSettings({ screenNumber: settings.screenNumber - 1 })} className="mt-2">
                    Back
                </IonButton>
            </div>
        </IonModal>
    );

    if (settings.screenNumber == 8)
    return (
        <IonModal isOpen={!settings.done} style={{'--height': 'fit-content', '--max-width': '26em' }}>
            <IonHeader>
                <IonToolbar>
                    <IonTitle>Congratulations!</IonTitle>
                </IonToolbar>
            </IonHeader>
            <div className="overflow-auto" style={{'maxHeight': '80vh', 'overflowY': 'auto'}}>
                <IonText className="markdown">
                    <p className="pt-2 px-3">
                        Congrats, you are now ready to start using the website!
                    </p>
                    <p className="pt-2 px-3">
                        For any questions, please feel free to reach out at <a href="mailto:contact@soclearn.org">contact@soclearn.org</a>. We're here to help!
                    </p>
                </IonText>

                <IonButton expand="full" onClick={(e) => {
                    // set screenNumber to the beginning so that next time this is opened,
                    // we get at the screen people would be most interested in
                    updateSettings({ done: true, screenNumber: 1 });
                    e.target.closest('ion-modal').dismiss()
                }} className="mt-2">
                    Done
                </IonButton>
                <IonButton fill="clear" expand="full" onClick={() => updateSettings({ screenNumber: settings.screenNumber - 1 })} className="mt-2">
                    Back
                </IonButton>
            </div>
        </IonModal>
    );
}

export default Onboarding;
